import React, {
	useState, useMemo, useRef, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// external components
import { Label } from 'components/Form';

import { SEARCH_LIMIT } from '../config';

// styles
import './SearchBar.scss';

let timeout;

const SearchBar = ({
	instance,
	handleSearch,
	handleClearSearch,
	onInputChange,
	showLabel = false,
	withSearchIcon = false,
	labelText = 'Have a question about this? Ask people who own it.',
	placeholderText = '',
	debounceMs = 500,
	searchTerm = '',
	showRightSearchIcon = false
}) => {

	const [ searchQuery, setSearchQuery ] = useState(searchTerm);
	const searchinstanceId = useMemo(() => `searchQuery_${instance}`, [ instance ]);
	const inputRef = useRef(null);
	const spaceEntered = useRef(false);
	const showClearButton = searchQuery.trim().length > 0;
	const handleInputChange = (e) => {

		const inputValue = e.target.value;
		setSearchQuery(e.target.value);
		if (typeof onInputChange === 'function') {

			onInputChange(e.target.value);

		}

		// do nothing if space key was entered
		if (spaceEntered.current) {

			return;

		}

		// call API only if search term doesn't exceed 500 character limit
		if (inputValue.length <= SEARCH_LIMIT) {

			window.clearTimeout(timeout);
			timeout = window.setTimeout(() => {

				const trimmedValue = inputValue.trim().replace(/\s?(?=\s{1})/gim, '');
				handleSearch(trimmedValue);

			}, debounceMs);

		}

	};
	const handleKeyDown = (e) => {

		// check if space key was entered
		if (e.key === ' ') {

			spaceEntered.current = true;

		} else {

			spaceEntered.current = false;

		}

	};

	useEffect(() => () => window.clearTimeout(timeout), []);

	return (
		<div className="SearchBar">
			<Label htmlFor={searchinstanceId} visuallyHidden={!showLabel}>
				{labelText}
			</Label>
			<div
				className={classNames('SearchBar__inputWrapper', {
					'SearchBar__inputWrapper--withSearchIcon': Boolean(withSearchIcon)
				})}
			>
				{withSearchIcon && <span className="SearchBar__icon lpIcon-search" />}
				<input
					id={searchinstanceId}
					name={searchinstanceId}
					type="text"
					className="SearchBar__input"
					placeholder={placeholderText}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					value={searchQuery}
					ref={inputRef}
					onPaste={(e) => {

						let pastedText = e.clipboardData?.getData('text') ?? '';
						pastedText = pastedText.trim().replace(/\s?(?=\s{1})/gim, '');
						if (pastedText.length <= 500) return;
						setSearchQuery(pastedText);
						// only send first 500 characters to API
						const truncatedQuery = pastedText.slice(0, 500);
						handleSearch(truncatedQuery);

					}}
				/>
				{showClearButton && (
					<button
						className="SearchBar__clearSearchBtn"
						type="button"
						onClick={() => {

							setSearchQuery('');
							inputRef.current.focus();
							handleClearSearch();

						}}
					>
						&times;
					</button>
				)}

				{!showClearButton && showRightSearchIcon && <span className="SearchBar__rightIcon lpIcon-search" />}
			</div>
		</div>
	);

};

SearchBar.propTypes = {
	instance: PropTypes.oneOf([ 'questionsAndAnswers', 'reviews' ]).isRequired,
	handleSearch: PropTypes.func.isRequired,
	handleClearSearch: PropTypes.func.isRequired,
	onInputChange: PropTypes.func,
	showLabel: PropTypes.bool,
	labelText: PropTypes.string,
	placeholderText: PropTypes.string,
	withSearchIcon: PropTypes.bool,
	debounceMs: PropTypes.number,
	searchTerm: PropTypes.string,
	showRightSearchIcon: PropTypes.bool
};

export default SearchBar;
